/**
 * MLCOItemDrawer.jsx: BM-H634 (26 Sep 2026).
 *
 * One MLCO queue item in full: the matter, the risk assessment that raised it,
 * why it is here, its history, and the fee earner and MLCO steps. Moved out of
 * MLCOInboxPage so the Compliance Dashboard's high risk drill-down opens the
 * same panel the MLCO Inbox does (Vanessa, 26 Sep 2026: "with ability to
 * drill down (in same way the card shows all details in the MLCO inbox)").
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldAlert, X } from "lucide-react";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
import MLCOReviewForm from "./MLCOReviewForm";
import COLPSignoffForm from "./COLPSignoffForm";
import RecordAMLAfterMLCOClear from "./RecordAMLAfterMLCOClear";

// BM-H631 (26 Sep 2026): the queue stores pending_fe -> pending_mlco -> closed
// (compliance/mlco_queue_router.py). This page was written for an older set of
// states (open / fe_responded / mlco_reviewing), so on real items no action
// button ever showed, the filter matched nothing and the totals read 0.
export const STATUS_LABEL = {
  pending_fe: "Waiting for fee earner",
  pending_mlco: "Waiting for MLCO",
  closed: "Closed",
};

const FE_ACTIONS = [
  { value: "documents_requested", label: "Documents requested" },
  { value: "documents_received", label: "Documents received" },
  { value: "client_questioned", label: "Client questioned" },
  { value: "manager_consulted", label: "Supervisor consulted" },
  { value: "other", label: "Other (explain, 20 characters or more)" },
];

const MLCO_OUTCOMES = [
  { value: "cleared", label: "Cleared" },
  { value: "hold_matter", label: "Hold the matter" },
  { value: "escalated", label: "Escalated" },
  { value: "reported", label: "Reported" },
];

// BM-H3681: the matter page panel names the decision the same way.
export function outcomeLabel(value) {
  return MLCO_OUTCOMES.find((o) => o.value === value)?.label || humanise(value || "");
}

// BM-H3548 (4 Oct 2026): the history read "Fe action" for the step the MLCO
// had taken to be her decision. Say whose step each entry was.
const HISTORY_LABEL = {
  enqueue: "Added to the inbox",
  fe_action: "Fee earner step",
  mlco_action: "MLCO decision",
  mlco_remediation_request: "Sent back to the fee earner",
};

function historyLabel(entry) {
  const label = HISTORY_LABEL[entry.action] || humanise(entry.action);
  if (entry.action === "mlco_action") {
    const outcome = MLCO_OUTCOMES.find((o) => o.value === entry.payload?.outcome);
    const skipped = entry.payload?.fee_earner_step_skipped ? " (before any fee earner step)" : "";
    return `${label}${outcome ? `: ${outcome.label}` : ""}${skipped}`;
  }
  return label;
}

const FLAG_LABEL = {
  INTAKE_HIGH_RISK_BAND: "Risk assessment rated at or above the MLCO threshold",
};

const FACTOR_LEVEL = { H: "High", M: "Medium", L: "Low" };

export const SEVERITY_BADGE = {
  critical: "bg-red-900/40 text-red-300 border-red-700/50",
  high: "bg-orange-900/40 text-orange-300 border-orange-700/50",
  medium: "bg-amber-900/30 text-amber-300 border-amber-700/40",
  low: "bg-slate-800 text-slate-300 border-slate-700/50",
};

export const STATUS_BADGE = {
  pending_fe: "bg-red-900/40 text-red-300 border-red-700/50",
  pending_mlco: "bg-amber-900/30 text-amber-300 border-amber-700/40",
  closed: "bg-emerald-900/30 text-emerald-300 border-emerald-700/40",
};

export function statusLabel(s) {
  return STATUS_LABEL[s] || (s || "").replace(/_/g, " ");
}

// BM-H3641 (Vanessa, 5 Oct 2026, on BL.RS.006.1537): "we need to fix this so
// the pipeline can move on with my approval". A hold closed the item, so the
// MLCO could never come back and approve it. A hold now leaves the item open
// (mlco_on_hold); an item closed on a hold before that fix can still be
// decided. Both read as "On hold".
export function isOnHold(item) {
  if (!item) return false;
  if (item.mlco_on_hold) return true;
  return item.status === "closed" && item.mlco_outcome === "hold_matter";
}

export const ON_HOLD_BADGE = "bg-amber-900/30 text-amber-300 border-amber-700/40";

export function itemStatusLabel(item) {
  return isOnHold(item) ? "On hold" : statusLabel(item?.status);
}

export function itemStatusBadge(item) {
  return isOnHold(item) ? ON_HOLD_BADGE : STATUS_BADGE[item?.status] || "";
}

function humanise(key) {
  const s = String(key || "").replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function authHeaders() {
  const email =
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    "";
  return email ? { "X-Bonsai-User-Id": email } : {};
}

// ── Detail drawer ──────────────────────────────────────────────

function ActionPanel({ item, userMeta, onActed }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [choice, setChoice] = useState("");

  const isMLCOOrCOLP = !!(userMeta?.is_mlco || userMeta?.is_colp);
  const status = item?.status || "";
  // BM-H3548 (Vanessa, 4 Oct 2026): "i recorded a decision but it still says
  // waiting for MLCO". A new item waits for the fee earner, and this panel
  // gave the MLCO the fee earner's form at that stage, so her decision was
  // saved as a fee earner action. The MLCO or COLP now gets the decision at
  // either stage; the server records whether the fee earner step was done.
  const onHold = isOnHold(item);
  const mlcoStep = isMLCOOrCOLP && (status === "pending_mlco" || status === "pending_fe" || onHold);
  const feStep = status === "pending_fe" && !mlcoStep;

  const submit = useCallback(async () => {
    if (!choice) {
      toast.error(feStep ? "Choose the action taken." : "Choose an outcome.");
      return;
    }
    if (!note.trim()) {
      toast.error("Please give a reason.");
      return;
    }
    setBusy(true);
    try {
      if (feStep) {
        await axios.post(
          `${API}/compliance/mlco-queue/${item.id}/fe-action`,
          { action_taken: choice, reason: note },
          { headers: authHeaders() }
        );
        toast.success("Action recorded. The item now waits for the MLCO.");
      } else {
        await axios.post(
          `${API}/compliance/mlco-queue/${item.id}/mlco-action`,
          { outcome: choice, reason: note },
          { headers: authHeaders() }
        );
        toast.success(
          choice === "hold_matter"
            ? "The matter is on hold. Come back here to decide it when you are ready."
            : "Decision recorded. The item is closed."
        );
      }
      setNote("");
      setChoice("");
      onActed && onActed();
    } catch (err) {
      toast.error(errorMessage(err, "Could not record this"));
    } finally {
      setBusy(false);
    }
  }, [choice, note, feStep, item, onActed]);

  if (!feStep && !mlcoStep) {
    return (
      <div className="text-xs text-slate-400" data-testid="mlco-action-panel-none">
        {onHold
          ? "This matter is on hold. Only the MLCO or COLP can decide it."
          : status === "closed"
          ? "This item is closed."
          : status === "pending_mlco"
            ? "Waiting for the MLCO or COLP to decide."
            : null}
      </div>
    );
  }

  const options = feStep ? FE_ACTIONS : MLCO_OUTCOMES;
  return (
    <div className="space-y-3" data-testid="mlco-action-panel">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">
        {feStep ? "Fee earner step: what has been done?" : "MLCO decision"}
      </div>
      {onHold && (
        <div
          className="rounded-md border border-amber-700/50 bg-amber-900/20 p-2 text-xs text-amber-100"
          data-testid="mlco-on-hold-note"
        >
          On hold{item.mlco_actioned_by ? ` (${item.mlco_actioned_by}` : ""}
          {item.mlco_actioned_by && item.mlco_actioned_at ? `, ${formatDate(item.mlco_actioned_at)})` : item.mlco_actioned_by ? ")" : ""}
          {item.mlco_outcome_reason ? `: ${item.mlco_outcome_reason}` : ""}. Record your
          decision here when you are ready.
        </div>
      )}
      {mlcoStep && status === "pending_fe" && !onHold && (
        <div className="text-xs text-slate-400" data-testid="mlco-fe-step-not-done">
          The fee earner has not recorded anything yet. You can decide now, or
          ask them to put something right below.
        </div>
      )}
      <select
        value={choice}
        onChange={(e) => setChoice(e.target.value)}
        className="w-full bg-slate-900/60 border border-slate-700/60 rounded-md p-2 text-sm text-slate-200"
        data-testid={feStep ? "mlco-fe-action-select" : "mlco-outcome-select"}
      >
        <option value="">{feStep ? "Choose the action taken" : "Choose an outcome"}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <textarea
        className="w-full bg-slate-900/60 border border-slate-700/60 rounded-md p-2 text-sm text-slate-200 placeholder-slate-500"
        rows={3}
        placeholder="Reason"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        data-testid="mlco-action-note"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={submit}
          className="px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-700/30 text-emerald-200 border border-emerald-700/60 hover:bg-emerald-700/50 disabled:opacity-50"
          data-testid={feStep ? "mlco-fe-submit-btn" : "mlco-outcome-submit-btn"}
        >
          {feStep ? "Record action" : "Record decision"}
        </button>
        {busy && <Loader2 size={14} className="text-slate-400 animate-spin" />}
      </div>
    </div>
  );
}

// BM-H3287 (30 Sep 2026), Vanessa: "when i comment on it and have a task for
// the fee earner to remediate something, i need that from the mlco card".
function RemediationRequest({ request }) {
  if (!request?.instruction) return null;
  return (
    <div
      className="rounded-md border border-amber-700/50 bg-amber-900/20 p-3 text-sm text-amber-100"
      data-testid="mlco-remediation-request"
    >
      <div className="text-[11px] uppercase tracking-wider text-amber-300/80">
        The MLCO has asked the fee earner to put this right
      </div>
      <div className="mt-1 whitespace-pre-wrap">{request.instruction}</div>
      <div className="mt-1 text-xs text-amber-300/70">
        {request.due ? `Due ${request.due}. ` : ""}
        {request.requested_by ? `Asked by ${request.requested_by}.` : ""}
      </div>
    </div>
  );
}

function RemediationPanel({ item, userMeta, onActed }) {
  const [busy, setBusy] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [due, setDue] = useState("");
  const isMLCOOrCOLP = !!(userMeta?.is_mlco || userMeta?.is_colp);
  const open = item?.status === "pending_mlco" || item?.status === "pending_fe";

  const submit = useCallback(async () => {
    if (!instruction.trim()) {
      toast.error("Say what the fee earner needs to put right.");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/compliance/mlco-queue/${item.id}/mlco-remediation-request`,
        { instruction: instruction.trim(), due: due || null },
        { headers: authHeaders() }
      );
      const n = res.data?.notification || {};
      if (n.status === "failed") {
        toast.error("Sent back to the fee earner, but the email to them failed. Tell them directly.");
      } else {
        toast.success("Sent back to the fee earner. It is on their task list and they have been emailed.");
      }
      setInstruction("");
      setDue("");
      onActed && onActed();
    } catch (err) {
      toast.error(errorMessage(err, "Could not send this back"));
    } finally {
      setBusy(false);
    }
  }, [instruction, due, item, onActed]);

  if (!isMLCOOrCOLP || !open) return null;
  return (
    <div className="space-y-2" data-testid="mlco-remediation-panel">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">
        Ask the fee earner to put something right
      </div>
      <textarea
        className="w-full bg-slate-900/60 border border-slate-700/60 rounded-md p-2 text-sm text-slate-200 placeholder-slate-500"
        rows={3}
        placeholder="Your comment and what they need to do"
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        data-testid="mlco-remediation-instruction"
      />
      <div className="flex items-center gap-2">
        <label className="text-xs text-slate-400" htmlFor="mlco-remediation-due">Due (optional)</label>
        <input
          id="mlco-remediation-due"
          type="date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          className="bg-slate-900/60 border border-slate-700/60 rounded-md px-2 py-1 text-xs text-slate-200"
          data-testid="mlco-remediation-due"
        />
        <button
          type="button"
          disabled={busy}
          onClick={submit}
          className="ml-auto px-3 py-1.5 rounded-md text-xs font-medium bg-amber-700/30 text-amber-100 border border-amber-700/60 hover:bg-amber-700/50 disabled:opacity-50"
          data-testid="mlco-remediation-submit-btn"
        >
          Send to fee earner
        </button>
        {busy && <Loader2 size={14} className="text-slate-400 animate-spin" />}
      </div>
    </div>
  );
}

// BM-H631: the risk assessment that raised the item, and a PDF copy of it.
function RiskAssessmentSection({ itemId, matterRef, ra }) {
  const [busy, setBusy] = useState(false);

  const download = useCallback(async () => {
    setBusy(true);
    try {
      const res = await axios.get(
        `${API}/compliance/mlco-queue/${itemId}/risk-assessment.pdf`,
        { headers: authHeaders(), responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${matterRef || "matter"} - Risk Assessment.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errorMessage(err, "Could not download the risk assessment"));
    } finally {
      setBusy(false);
    }
  }, [itemId, matterRef]);

  if (!ra.found) {
    return (
      <div
        className="rounded-md p-3 bg-red-950/40 border border-red-800/50 text-red-200 text-xs"
        data-testid="mlco-risk-assessment-missing"
      >
        This item names a risk assessment ({ra.id}) that is no longer on record.
      </div>
    );
  }

  const factors = Object.entries(ra.risk_factors || {});
  const text = [
    ["Risk narrative", ra.risk_narrative],
    ["Mitigating actions", ra.mitigating_actions],
    ["Material risks", ra.material_risks],
    ["Supervisor notes", ra.supervisor_notes],
  ].filter(([, v]) => v);

  return (
    <div className="space-y-3" data-testid="mlco-risk-assessment">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">
          Risk assessment that raised this
        </div>
        <button
          type="button"
          onClick={download}
          disabled={busy}
          className="px-2.5 py-1 rounded-md text-xs bg-slate-800 text-slate-200 border border-slate-700/60 hover:bg-slate-700 disabled:opacity-50"
          data-testid="mlco-risk-assessment-download"
        >
          {busy ? "Preparing…" : "Download a copy (PDF)"}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 text-slate-200">
        <div>
          <div className="text-[11px] text-slate-500">Band</div>
          <div data-testid="mlco-risk-assessment-band">{ra.band || "—"}</div>
        </div>
        <div>
          <div className="text-[11px] text-slate-500">Score</div>
          <div>{ra.total_score ?? "—"}</div>
        </div>
        <div>
          <div className="text-[11px] text-slate-500">Assessed</div>
          <div>{formatDate(ra.assessed_at)}</div>
        </div>
        <div>
          <div className="text-[11px] text-slate-500">Assessed by</div>
          <div className="break-all">{ra.assessed_by || "Not recorded"}</div>
        </div>
      </div>
      {factors.length > 0 && (
        <table className="w-full text-xs" data-testid="mlco-risk-assessment-factors">
          <tbody>
            {factors.map(([k, v]) => (
              <tr key={k} className="border-t border-slate-800">
                <td className="py-1 pr-2 text-slate-400">{humanise(k)}</td>
                <td className="py-1 text-slate-200">{FACTOR_LEVEL[v] || String(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {ra.risk_triggers && ra.risk_triggers.length > 0 && (
        <div>
          <div className="text-[11px] text-slate-500">Why it scored as it did</div>
          <ul className="list-disc ml-4 text-xs text-slate-300">
            {ra.risk_triggers.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </div>
      )}
      {text.map(([label, value]) => (
        <div key={label}>
          <div className="text-[11px] text-slate-500">{label}</div>
          <div className="text-xs text-slate-200 whitespace-pre-wrap">{value}</div>
        </div>
      ))}
    </div>
  );
}

function FlagEvidence({ evidence }) {
  if (!evidence) return null;
  if (typeof evidence === "string") {
    return <div className="text-[11px] text-slate-400 mt-0.5">{evidence}</div>;
  }
  const skip = new Set(["assessment_store", "assessment_id"]);
  const rows = Object.entries(evidence).filter(
    ([k, v]) => !skip.has(k) && v !== null && v !== "" && typeof v !== "object"
  );
  if (!rows.length) return null;
  return (
    <div className="text-[11px] text-slate-400 mt-0.5 space-y-0.5">
      {rows.map(([k, v]) => (
        <div key={k}>
          <span className="text-slate-500">{humanise(k)}: </span>
          {k.endsWith("_at") ? formatDate(v) : String(v)}
        </div>
      ))}
    </div>
  );
}

export default function DetailDrawer({ itemId, userMeta, onClose, onActed }) {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${API}/compliance/mlco-queue/${itemId}`,
        { headers: authHeaders() }
      );
      setDetail(res.data);
    } catch (err) {
      const msg = errorMessage(err, "Failed to load detail");
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div
      className="fixed inset-y-0 right-0 w-full max-w-xl bg-slate-900 border-l border-slate-700/60 shadow-2xl overflow-y-auto z-40"
      data-testid="mlco-detail-drawer"
      role="dialog"
      aria-label="MLCO queue item detail"
    >
      <div className="flex items-center justify-between p-4 border-b border-slate-700/60">
        <div className="flex items-center gap-2">
          <ShieldAlert size={16} className="text-amber-300" />
          <h3 className="text-sm font-semibold text-slate-100">
            MLCO Queue Item
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-slate-400 hover:text-slate-100"
          data-testid="mlco-detail-close-btn"
          aria-label="Close detail"
        >
          <X size={16} />
        </button>
      </div>

      {loading && (
        <div className="p-6 flex items-center gap-2 text-slate-400 text-sm" data-testid="mlco-detail-loading">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      )}
      {error && (
        <div className="p-4 m-4 rounded-md bg-red-950/40 border border-red-800/50 text-red-200 text-sm" data-testid="mlco-detail-error">
          {error}
        </div>
      )}
      {!loading && !error && detail && (
        <div className="p-4 space-y-4 text-sm">
          <div className="space-y-1">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Matter</div>
            <div className="text-slate-100 font-medium" data-testid="mlco-detail-matter-ref">
              {detail.matter_ref || "—"}
            </div>
            <div className="text-slate-400">{detail.matter_name || ""}</div>
          </div>

          {/* BM-H633: who the client is and what the matter is. */}
          <div className="grid grid-cols-2 gap-3" data-testid="mlco-detail-matter-summary">
            {[
              ["Client", "client_name"],
              ["Matter", "matter_description"],
              ["Fee earner", "fee_earner"],
              ["Matter type", "matter_type"],
            ].map(([label, key]) => (
              <div key={key} className={key === "matter_description" ? "col-span-2" : ""}>
                <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
                <div
                  className={detail.matter?.[key] ? "text-slate-200 mt-1" : "text-amber-300 mt-1"}
                  data-testid={`mlco-detail-${key}`}
                >
                  {detail.matter?.[key] || "Not recorded"}
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Status</div>
              <span className={`inline-block mt-1 px-2 py-0.5 rounded-md text-[11px] border ${itemStatusBadge(detail)}`} data-testid="mlco-detail-status">
                {itemStatusLabel(detail)}
              </span>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Severity</div>
              <span className={`inline-block mt-1 px-2 py-0.5 rounded-md text-[11px] border ${SEVERITY_BADGE[detail.severity] || ""}`} data-testid="mlco-detail-severity">
                {detail.severity}
              </span>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Raised</div>
              <div className="text-slate-200 mt-1">{formatDate(detail.created_at)}</div>
            </div>
          </div>

          {detail.risk_assessment && (
            <div className="pt-2 border-t border-slate-700/60">
              <RiskAssessmentSection
                itemId={detail.id}
                matterRef={detail.matter_ref}
                ra={detail.risk_assessment}
              />
            </div>
          )}

          {detail.flags && detail.flags.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Why it is here</div>
              <ul className="space-y-1 text-slate-200" data-testid="mlco-detail-flags">
                {detail.flags.map((flag, i) => (
                  <li key={i} className="border border-slate-700/60 rounded-md p-2 bg-slate-900/40">
                    <div className="text-xs text-amber-200">{FLAG_LABEL[flag.code] || flag.code || "Flag"}</div>
                    <FlagEvidence evidence={flag.evidence} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {detail.audit_trail && detail.audit_trail.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">History</div>
              <ul className="space-y-1.5 text-xs text-slate-300" data-testid="mlco-detail-history">
                {detail.audit_trail.slice(0, 20).map((entry, i) => (
                  <li key={entry.id || i} className="border-l-2 border-slate-700 pl-2">
                    <span className="text-slate-100">{historyLabel(entry)}</span>
                    <span className="text-slate-500"> · {formatDate(entry.ts)} · {entry.actor_email}</span>
                    {entry.payload?.reason && <div className="text-slate-400">{entry.payload.reason}</div>}
                    {entry.payload?.instruction && <div className="text-slate-400">{entry.payload.instruction}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="pt-2 border-t border-slate-700/60 space-y-3">
            <RemediationRequest request={detail.mlco_remediation_request} />
            <ActionPanel
              item={detail}
              userMeta={userMeta}
              onActed={() => { load(); onActed && onActed(); }}
            />
            <RemediationPanel
              item={detail}
              userMeta={userMeta}
              onActed={() => { load(); onActed && onActed(); }}
            />
            {/* BM-H3733: clearing does not touch a referred Thirdfort review,
                so ask the MLCO whether to record AML as approved. */}
            {(userMeta?.is_mlco || userMeta?.is_colp) && detail.mlco_outcome === "cleared"
              && !isOnHold(detail) && detail.matter_ref && (
              <RecordAMLAfterMLCOClear matterRef={detail.matter_ref} headers={authHeaders} />
            )}
          </div>

          {detail.matter_review_id && (userMeta?.is_mlco || userMeta?.is_colp) && (
            <div
              className="pt-3 border-t border-slate-700/60"
              data-testid="mlco-decision-form-wrapper"
            >
              {detail.matter_review_type === "colp_signoff" ? (
                <>
                  <div className="text-[11px] uppercase tracking-wider text-purple-300 mb-2">
                    COLP sign-off
                  </div>
                  {userMeta?.is_colp ? (
                    <COLPSignoffForm
                      reviewId={detail.matter_review_id}
                      userMeta={userMeta}
                      onDecided={() => { load(); onActed && onActed(); }}
                      onCancel={onClose}
                    />
                  ) : (
                    <div
                      className="text-xs text-slate-400 italic"
                      data-testid="colp-readonly-notice"
                    >
                      This matter is awaiting COLP sign-off. Read-only for MLCO.
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
                    MLCO decision
                  </div>
                  <MLCOReviewForm
                    reviewId={detail.matter_review_id}
                    userMeta={userMeta}
                    matterReference={detail.matter_ref}
                    onDecided={() => { load(); onActed && onActed(); }}
                    onCancel={onClose}
                  />
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
